import {
  ScheduleExercise,
  SchedulesResponse
} from '@/redux/schedules/types'
import { IAssignScheduleResponse, ManageType, UserRole } from '@/types'
import { httpClient } from './httpClient'

interface AssignScheduleAPIRequestPayload {
  totalDays: number
  totalCalendarDays: number
  startDate: string
  manageType: ManageType
  exercises: ScheduleExercise[]
  patientId: number
  allowMultipleSchedules: boolean
}

export async function assignSchedule ({
  totalDays,
  totalCalendarDays,
  startDate,
  patientId,
  manageType,
  exercises,
  allowMultipleSchedules
}: AssignScheduleAPIRequestPayload) {
  try {
    const response: IAssignScheduleResponse | undefined = await httpClient({
      baseURL: import.meta.env.VITE_API_MAIN_BASEURL,
      url: import.meta.env.VITE_CREATE_SCHEDULE_ENDPOINT,
      method: 'POST',
      withCredentials: true,
      data: {
        totalCalendarDays,
        startDate,
        patientId,
        totalDays,
        manageType,
        exercises,
        allowMultipleSchedules
      }
    })

    if (!response) throw Error

    return response
  } catch (error) {
    console.error('assignSchedule - Error: ', error)
    throw error
  }
}

export async function getSchedulesByPatient ({
  patientId
}: {
  patientId: number
}) {
  try {
    let url = import.meta.env.VITE_GET_SCHEDULES_BY_PATIENT_ENDPOINT
    url = url.replace('__patientId__', patientId.toString())

    const response = await httpClient({
      baseURL: import.meta.env.VITE_API_MAIN_BASEURL,
      url,
      withCredentials: true,
      method: 'GET',
      cache: false
    })

    return schedulesToModel(response)
  } catch (error) {
    console.error('getSchedulesByPatient - Error: ', error)
    throw error
  }
}

export async function getMySchedules () {
  try {
    const response = await httpClient({
      baseURL: import.meta.env.VITE_API_PATIENT_BASEURL,
      url: import.meta.env.VITE_GET_MY_SCHEDULES_ENDPOINT,
      withCredentials: true,
      method: 'GET',
      cache: false
    })

    return schedulesToModel(response)
  } catch (error) {
    console.error('getMySchedules - Error: ', error)
    throw error
  }
}

export async function getSingleSchedule ({ id, roles }: { id: number, roles: UserRole[] }) {
  try {
    let url = import.meta.env.VITE_GET_SCHEDULE_ENDPOINT
    url = url.replace('__scheduleId__', id.toString())

    const response = await httpClient({
      baseURL: roles.includes('doctor')
        ? import.meta.env.VITE_API_MAIN_BASEURL
        : import.meta.env.VITE_API_PATIENT_BASEURL,
      url,
      method: 'GET',
      cache: false,
      withCredentials: true
    })

    return response.data
  } catch (error) {
    console.error('getSingleSchedule - Error: ', error)
    throw error
  }
}

export async function editSchedule ({
  id,
  requestPayload
}: {
  id: number
  requestPayload: {
    totalDays?: number
    totalCalendarDays?: number
  }
}) {
  try {
    let url = import.meta.env.VITE_EDIT_SCHEDULE_ENDPOINT
    url = url.replace('__scheduleId__', id.toString())

    const response = await httpClient({
      baseURL: import.meta.env.VITE_API_MAIN_BASEURL,
      url,
      method: 'PUT',
      withCredentials: true,
      data: requestPayload
    })

    return response.data
  } catch (error) {
    console.error('editSchedule - Error: ', error)
    throw error
  }
}

export async function deleteSchedule ({ id }: { id: number }) {
  try {
    let url = import.meta.env.VITE_DELETE_SCHEDULE_ENDPOINT
    url = url.replace('__scheduleId__', id.toString())

    const response = await httpClient({
      baseURL: import.meta.env.VITE_API_MAIN_BASEURL,
      url,
      method: 'DELETE',
      withCredentials: true
    })

    return response.data
  } catch (error) {
    console.error('deleteSchedule - Error: ', error)
    throw error
  }
}

function schedulesToModel (schedulesResponse: SchedulesResponse) {
  return schedulesResponse.data.map(item => {
    const { scheduledExercises, ...rest } = item

    return rest
  })
}
